import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ShieldCheckIcon, UsersIcon, EyeIcon, UserPlusIcon, CheckCircleIcon,
  ChartBarIcon, ImagesIcon, SpinnerGapIcon, SealCheckIcon, XCircleIcon,
  ProhibitIcon, ArrowCounterClockwiseIcon, EyeSlashIcon, GaugeIcon, LightningIcon,
} from "@phosphor-icons/react";
import { fetchAdminStats, fetchAdminRemovals, restoreRemoval, hideRemoval, fetchAdminSettings, saveAdminSettings, fetchTrashGraveyard, clearGraveyard, fetchCuratedWallets, addCuratedWallet, deleteCuratedWallet, fetchAdminMetrics, fetchAdminIssues, resolveIssue, fetchHeliusUsers, setHeliusUserAccess } from "../lib/api";
import { shortAddr } from "../lib/solana";
import { useAuth } from "../context/AuthContext";
import { SkullIcon, TrophyIcon, PlusIcon, TrashIcon, TargetIcon, ArrowUpIcon, ArrowDownIcon, BugIcon, PulseIcon } from "@phosphor-icons/react";

const Curated = () => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["curatedWallets"], queryFn: fetchCuratedWallets });
  const [addr, setAddr] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const wallets = data?.wallets || [];

  const add = async () => {
    if (!addr.trim()) return;
    setBusy(true);
    try {
      await addCuratedWallet(addr.trim(), label.trim());
      qc.invalidateQueries({ queryKey: ["curatedWallets"] });
      qc.invalidateQueries({ queryKey: ["leaderboard"] });
      setAddr(""); setLabel("");
      toast.success("Curated wallet added — leaderboard will refresh");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add"); }
    setBusy(false);
  };

  const remove = async (a) => {
    try {
      await deleteCuratedWallet(a);
      qc.invalidateQueries({ queryKey: ["curatedWallets"] });
      qc.invalidateQueries({ queryKey: ["leaderboard"] });
      toast.success("Removed from curated");
    } catch { toast.error("Failed to remove"); }
  };

  return (
    <div className="border border-white/10 bg-card p-5" data-testid="curated-wallets">
      <div className="flex items-center gap-2 mb-1">
        <TrophyIcon size={20} weight="bold" className="text-neon" />
        <h3 className="font-heading font-bold text-lg">Curated Leaderboard Wallets</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Featured traders that always appear on the public Leaderboard (with live on-chain PnL). Add well-known KOLs so new
        visitors land on a populated, credible board. Changes refresh the board within the hour.
      </p>
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <input
          data-testid="curated-address-input"
          value={addr}
          onChange={(e) => setAddr(e.target.value)}
          placeholder="Solana wallet address"
          className="flex-1 bg-black/40 border border-white/15 px-3 py-2 text-sm font-mono focus:border-neon/50 outline-none"
        />
        <input
          data-testid="curated-label-input"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Label (e.g. Ansem)"
          className="sm:w-48 bg-black/40 border border-white/15 px-3 py-2 text-sm focus:border-neon/50 outline-none"
        />
        <button
          data-testid="curated-add-btn"
          onClick={add}
          disabled={busy || !addr.trim()}
          className="flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-semibold bg-neon text-black hover:bg-neon/90 transition-colors disabled:opacity-50"
        >
          {busy ? <SpinnerGapIcon size={15} className="animate-spin" /> : <PlusIcon size={15} weight="bold" />} Add
        </button>
      </div>
      {isLoading ? (
        <div className="flex justify-center py-8"><SpinnerGapIcon size={24} className="animate-spin text-neon" /></div>
      ) : wallets.length === 0 ? (
        <div className="py-8 text-center text-muted-foreground font-mono text-sm">No curated wallets.</div>
      ) : (
        <div className="divide-y divide-white/5">
          {wallets.map((w, i) => (
            <div key={w.address} data-testid={`curated-row-${i}`} className="flex items-center gap-3 py-2.5">
              <div className="min-w-0 flex-1">
                <div className="font-heading font-bold text-sm">{w.label || "—"}</div>
                <div className="font-mono text-[11px] text-muted-foreground truncate">{shortAddr(w.address)}</div>
              </div>
              <button
                data-testid={`curated-remove-${i}`}
                onClick={() => remove(w.address)}
                className="p-1.5 text-muted-foreground hover:text-danger transition-colors"
                title="Remove from curated"
              >
                <TrashIcon size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Graveyard = () => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["trashGraveyard"], queryFn: fetchTrashGraveyard, refetchInterval: 60000 });
  const [busy, setBusy] = useState("");
  const items = data?.items || [];
  const communityMin = data?.communityMin || 3;

  const clear = async (mint) => {
    setBusy(mint);
    try {
      await clearGraveyard(mint);
      qc.invalidateQueries({ queryKey: ["trashGraveyard"] });
      qc.invalidateQueries({ queryKey: ["communityTrash"] });
      toast.success("Cleared from graveyard");
    } catch { toast.error("Failed to clear"); }
    setBusy("");
  };

  return (
    <div className="border border-white/10 bg-card p-5" data-testid="trash-graveyard">
      <div className="flex items-center gap-2 mb-1">
        <SkullIcon size={20} weight="fill" className="text-danger" />
        <h3 className="font-heading font-bold text-lg">Trash Graveyard</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Tokens flagged as trash/scam by users. At <span className="text-danger font-semibold">{communityMin}+ distinct users</span> a
        token is auto-branded <span className="font-mono text-danger">☠️ SCAM</span> for everyone. Clear a token if it was wrongly flagged.
      </p>
      {isLoading ? (
        <div className="flex justify-center py-10"><SpinnerGapIcon size={26} className="animate-spin text-danger" /></div>
      ) : items.length === 0 ? (
        <div className="py-10 text-center text-muted-foreground font-mono text-sm">No tokens flagged yet.</div>
      ) : (
        <div className="divide-y divide-white/5">
          {items.map((it, i) => {
            const isScam = it.count >= communityMin;
            return (
              <div key={it.mint} data-testid={`graveyard-row-${i}`} className="flex items-center gap-3 py-3">
                <span className={`font-mono text-xs px-2 py-1 border ${isScam ? "bg-danger/25 border-danger/60 text-danger font-bold" : "bg-white/5 border-white/10 text-muted-foreground"}`}>
                  {isScam ? "☠️ SCAM" : "🗑️"} {it.count}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-heading font-bold text-sm">${it.symbol || "?"}</div>
                  <div className="font-mono text-[11px] text-muted-foreground truncate">{shortAddr(it.mint)}</div>
                </div>
                <a href={`https://dexscreener.com/solana/${it.mint}`} target="_blank" rel="noreferrer" className="text-xs text-neon hover:underline shrink-0">chart</a>
                <button
                  data-testid={`graveyard-clear-${i}`}
                  onClick={() => clear(it.mint)}
                  disabled={busy === it.mint}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-white/15 text-muted-foreground hover:border-neon/50 hover:text-neon transition-colors disabled:opacity-50 shrink-0"
                >
                  {busy === it.mint ? <SpinnerGapIcon size={13} className="animate-spin" /> : <ArrowCounterClockwiseIcon size={13} />} Clear
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, accent = "#00E599", testid }) => (
  <div data-testid={testid} className="border border-white/10 bg-card p-5">
    <div className="flex items-center gap-2 text-muted-foreground text-xs font-mono uppercase tracking-wide">
      <Icon size={15} style={{ color: accent }} /> {label}
    </div>
    <div className="font-heading font-black text-3xl mt-2" style={{ color: accent }}>{value ?? "—"}</div>
  </div>
);

const Overview = ({ stats }) => {
  const max = Math.max(1, ...(stats.viewsByDay || []).map((d) => d.count));
  const maxFeat = Math.max(1, ...(stats.topFeatures || []).map((f) => f.count));
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard testid="stat-total-users" icon={UsersIcon} label="Users" value={stats.totalUsers} />
        <StatCard testid="stat-verified" icon={CheckCircleIcon} label="Verified" value={stats.verifiedUsers} accent="#F0C000" />
        <StatCard testid="stat-new7d" icon={UserPlusIcon} label="New (7d)" value={stats.newUsers7d} accent="#7C5CFF" />
        <StatCard testid="stat-views" icon={EyeIcon} label="Page views" value={stats.totalPageViews} accent="#00E599" />
        <StatCard testid="stat-unique" icon={ChartBarIcon} label="Unique visitors" value={stats.uniqueVisitors} accent="#FF7A45" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-white/10 bg-card p-5">
          <h3 className="font-heading font-bold text-sm mb-4">Traffic — last 14 days</h3>
          {(stats.viewsByDay || []).length === 0 ? (
            <p className="text-muted-foreground font-mono text-xs py-8 text-center">No traffic recorded yet.</p>
          ) : (
            <div className="flex items-end gap-1.5 h-40">
              {stats.viewsByDay.map((d) => (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group">
                  <div className="w-full bg-neon/70 hover:bg-neon transition-colors" style={{ height: `${(d.count / max) * 100}%`, minHeight: "3px" }} title={`${d.date}: ${d.count}`} />
                  <span className="text-[9px] text-muted-foreground font-mono rotate-45 origin-left mt-1">{d.date.slice(5)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border border-white/10 bg-card p-5">
          <h3 className="font-heading font-bold text-sm mb-4">Most-used features</h3>
          {(stats.topFeatures || []).length === 0 ? (
            <p className="text-muted-foreground font-mono text-xs py-8 text-center">No data yet.</p>
          ) : (
            <div className="space-y-2">
              {stats.topFeatures.map((f) => (
                <div key={f.path} className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-white">{f.label}</span>
                    <span className="font-mono text-muted-foreground">{f.count}</span>
                  </div>
                  <div className="h-1.5 bg-white/5"><div className="h-full bg-neon/60" style={{ width: `${(f.count / maxFeat) * 100}%` }} /></div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Users = ({ stats }) => (
  <div className="border border-white/10 bg-card overflow-hidden">
    <div className="grid grid-cols-[1fr_auto_auto] gap-3 px-4 py-3 border-b border-white/10 text-xs font-mono uppercase text-muted-foreground">
      <span>Email</span><span>Verified</span><span>Joined</span>
    </div>
    {(stats.registrations || []).length === 0 ? (
      <p className="text-muted-foreground font-mono text-sm py-12 text-center">No registrations yet.</p>
    ) : (
      stats.registrations.map((u, i) => (
        <div key={i} data-testid={`admin-user-row-${i}`} className="grid grid-cols-[1fr_auto_auto] gap-3 px-4 py-2.5 border-b border-white/5 text-sm items-center">
          <span className="truncate font-mono">{u.email}</span>
          <span>{u.verified ? <SealCheckIcon size={16} weight="fill" className="text-neon" /> : <XCircleIcon size={16} className="text-muted-foreground" />}</span>
          <span className="font-mono text-xs text-muted-foreground">{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : "—"}</span>
        </div>
      ))
    )}
  </div>
);

const Media = () => (
  <div className="border border-white/10 bg-card p-10 text-center space-y-3">
    <ImagesIcon size={40} className="text-muted-foreground mx-auto" />
    <h3 className="font-heading font-bold text-lg">Media Library</h3>
    <p className="text-muted-foreground text-sm max-w-md mx-auto">
      Upload logos, screenshots and promo images with SEO alt-text to boost your Google ranking. This is the next update — it needs file storage wired up. Say the word and I'll build it.
    </p>
  </div>
);

const Removals = () => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["adminRemovals"], queryFn: fetchAdminRemovals, refetchInterval: 30000 });
  const [busy, setBusy] = useState("");

  const act = async (wallet, hidden) => {
    setBusy(wallet);
    try {
      if (hidden) { await restoreRemoval(wallet); toast.success("Wallet restored to the Playbook"); }
      else { await hideRemoval(wallet); toast.success("Wallet hidden from the Playbook"); }
      qc.invalidateQueries({ queryKey: ["adminRemovals"] });
      qc.invalidateQueries({ queryKey: ["hiddenWallets"] });
    } catch {
      toast.error("Action failed — try again");
    } finally { setBusy(""); }
  };

  if (isLoading) return <div className="flex justify-center py-16"><SpinnerGapIcon size={28} className="animate-spin text-neon" /></div>;
  const reqs = data?.requests || [];

  return (
    <div className="border border-white/10 bg-card overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <span className="text-xs font-mono uppercase text-muted-foreground">Removal requests</span>
        <span className="text-xs font-mono text-danger">{data?.hiddenCount || 0} hidden</span>
      </div>
      {reqs.length === 0 ? (
        <p className="text-muted-foreground font-mono text-sm py-12 text-center">No removal requests yet.</p>
      ) : (
        reqs.map((r, i) => (
          <div key={`${r.wallet}-${i}`} data-testid={`admin-removal-row-${i}`} className="flex items-center gap-3 px-4 py-3 border-b border-white/5">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-sm">{shortAddr(r.wallet)}</span>
                {r.handle && <span className="font-mono text-[11px] text-muted-foreground">{r.handle}</span>}
                {r.hidden
                  ? <span className="text-[10px] font-mono px-1.5 py-0.5 bg-danger/15 text-danger border border-danger/30">HIDDEN</span>
                  : <span className="text-[10px] font-mono px-1.5 py-0.5 bg-neon/15 text-neon border border-neon/30">VISIBLE</span>}
              </div>
              {r.reason && <p className="text-xs text-muted-foreground mt-1 truncate">{r.reason}</p>}
              <div className="flex items-center gap-3 mt-0.5 text-[10px] font-mono text-muted-foreground/70">
                {r.contact && <span>{r.contact}</span>}
                {r.ts && <span>{new Date(r.ts).toLocaleString()}</span>}
              </div>
            </div>
            <button
              data-testid={`admin-removal-toggle-${i}`}
              onClick={() => act(r.wallet, r.hidden)}
              disabled={busy === r.wallet}
              className={`flex items-center gap-1.5 text-xs px-3 py-2 font-semibold transition-colors disabled:opacity-40 whitespace-nowrap ${r.hidden ? "border border-neon/40 text-neon hover:bg-neon/10" : "border border-danger/40 text-danger hover:bg-danger/10"}`}
            >
              {r.hidden ? <><ArrowCounterClockwiseIcon size={13} weight="bold" /> Restore</> : <><EyeSlashIcon size={13} weight="bold" /> Hide</>}
            </button>
          </div>
        ))
      )}
    </div>
  );
};

const CreditMeter = ({ usage }) => {
  if (!usage) return null;
  const pct = Math.min(100, usage.pctUsed || 0);
  const warn = pct >= 80;
  const maxF = Math.max(1, ...(usage.byFeature || []).map((f) => f.credits || 0));
  return (
    <div className="border border-white/10 bg-card p-5" data-testid="credit-meter">
      <div className="flex items-center gap-2 mb-1">
        <PulseIcon size={18} weight="bold" className="text-neon" />
        <h3 className="font-heading font-bold text-sm">Helius Credit Meter — {usage.month}</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
        Estimated credits burned this month, counted locally per successful Helius call (enhanced ≈ 10 credits, RPC ≈ 1).
        Helius doesn't expose live balance via API — treat this as a close estimate, and cross-check at dashboard.helius.dev.
      </p>
      <div className="flex items-end gap-4 flex-wrap mb-2">
        <div className="font-heading font-black text-3xl leading-none" data-testid="credit-meter-total">
          {(usage.totalCredits || 0).toLocaleString()}
        </div>
        <div className="text-xs font-mono text-muted-foreground pb-0.5">
          est. credits · {(usage.totalCalls || 0).toLocaleString()} calls · {pct}% of {Math.round(usage.budget / 1e6)}M budget
        </div>
      </div>
      <div className="h-2 bg-white/5 mb-4">
        <div className={warn ? "h-full bg-danger" : "h-full bg-neon"} style={{ width: `${pct}%`, minWidth: usage.totalCredits ? "2px" : 0 }} />
      </div>
      {(usage.byFeature || []).length === 0 ? (
        <p className="text-xs text-muted-foreground font-mono">No Helius calls counted yet this month.</p>
      ) : (
        <div className="space-y-2">
          {usage.byFeature.map((f) => (
            <div key={f.feature} className="space-y-1" data-testid={`credit-feature-${f.feature}`}>
              <div className="flex justify-between text-xs">
                <span className="text-white">{f.feature}</span>
                <span className="font-mono text-muted-foreground">{(f.credits || 0).toLocaleString()} cr · {(f.calls || 0).toLocaleString()} calls</span>
              </div>
              <div className="h-1.5 bg-white/5"><div className="h-full bg-neon/60" style={{ width: `${((f.credits || 0) / maxF) * 100}%` }} /></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const heliusAgo = (ts) => {
  if (!ts) return "—";
  const s = Math.floor(Date.now() / 1000 - ts);
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const HeliusAccess = () => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["heliusUsers"], queryFn: fetchHeliusUsers, refetchInterval: 30000 });
  const [busy, setBusy] = useState("");

  const toggle = async (u) => {
    setBusy(u.id);
    try {
      await setHeliusUserAccess(u.id, !u.access);
      toast.success(!u.access ? `Unlocked Helius features for ${u.email}` : `Locked ${u.email} out of Helius features`);
      qc.invalidateQueries({ queryKey: ["heliusUsers"] });
    } catch {
      toast.error("Couldn't update — try again");
    } finally { setBusy(""); }
  };

  if (isLoading || !data) return <div className="flex justify-center py-16"><SpinnerGapIcon size={28} className="animate-spin text-neon" /></div>;
  const pending = data.users.filter((u) => u.requestedAt && !u.access);
  const granted = data.users.filter((u) => u.access && u.role !== "admin").length;
  const total = data.users.reduce((s, u) => s + (u.creditsUsed || 0), 0);
  const topFeature = (f) => {
    const e = Object.entries(f || {}).sort((a, b) => b[1] - a[1])[0];
    return e ? `${e[0]} (${e[1].toLocaleString()})` : "—";
  };

  return (
    <div className="space-y-5" data-testid="admin-helius-access">
      <div className="border border-white/10 p-5 space-y-2">
        <div className="flex items-center gap-2">
          <LightningIcon size={20} className="text-gold" />
          <h2 className="font-heading font-bold text-lg">Helius Access Control</h2>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed max-w-2xl">
          Everyone starts locked. Tick a user to unlock Smart Money, Wallet Scout, Funding Web, PnL and
          token-flow lookups for them — their estimated credit spend shows here so you can lock out heavy
          users late in the month. Your admin account always has full access. Credits refresh on <span className="text-white font-semibold">{data.resetsOn}</span>.
        </p>
        <div className="flex flex-wrap gap-5 pt-1 text-xs font-mono">
          <span data-testid="helius-pending-count">⏳ Pending requests: <b className="text-gold">{pending.length}</b></span>
          <span data-testid="helius-granted-count">✅ Approved users: <b className="text-neon">{granted}</b></span>
          <span data-testid="helius-total-used">🔥 Est. user credits this period: <b className="text-white">{total.toLocaleString()}</b></span>
        </div>
      </div>

      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-left text-xs text-muted-foreground font-mono">
              <th className="px-4 py-2.5">User</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5 text-right">Credits used</th>
              <th className="px-4 py-2.5 text-right">Calls</th>
              <th className="px-4 py-2.5">Top feature</th>
              <th className="px-4 py-2.5">Last used</th>
              <th className="px-4 py-2.5 text-center">Access</th>
            </tr>
          </thead>
          <tbody>
            {data.users.map((u) => (
              <tr key={u.id} className={`border-b border-white/5 ${u.requestedAt && !u.access ? "bg-gold/5" : ""}`} data-testid={`helius-user-row-${u.id}`}>
                <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                <td className="px-4 py-3">
                  {u.role === "admin" ? (
                    <span className="text-[10px] font-bold px-2 py-0.5 bg-neon/15 text-neon">ADMIN · ALWAYS ON</span>
                  ) : u.requestedAt && !u.access ? (
                    <span className="text-[10px] font-bold px-2 py-0.5 bg-gold/15 text-gold animate-pulse">REQUESTED ACCESS</span>
                  ) : u.access ? (
                    <span className="text-[10px] font-bold px-2 py-0.5 bg-neon/10 text-neon">APPROVED</span>
                  ) : (
                    <span className="text-[10px] font-bold px-2 py-0.5 bg-white/5 text-muted-foreground">LOCKED</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right font-mono text-xs">{(u.creditsUsed || 0).toLocaleString()}</td>
                <td className="px-4 py-3 text-right font-mono text-xs">{u.calls || 0}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{topFeature(u.features)}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground font-mono">{heliusAgo(u.lastUsed)}</td>
                <td className="px-4 py-3 text-center">
                  {u.role === "admin" ? (
                    <CheckCircleIcon size={20} className="text-neon mx-auto" />
                  ) : (
                    <button
                      data-testid={`helius-toggle-${u.id}`}
                      onClick={() => toggle(u)}
                      disabled={busy === u.id}
                      className={`w-11 h-6 relative transition-colors ${u.access ? "bg-neon" : "bg-white/15"} disabled:opacity-50`}
                      title={u.access ? "Revoke Helius access" : "Grant Helius access"}
                    >
                      <span className={`absolute top-0.5 w-5 h-5 bg-black transition-all ${u.access ? "left-[22px]" : "left-0.5"}`} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!data.users.length && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground text-sm">No registered users yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const CreditsSaver = () => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["adminSettings"], queryFn: fetchAdminSettings });
  const [saving, setSaving] = useState("");

  const pick = async (mode) => {
    setSaving(mode);
    try {
      await saveAdminSettings(mode);
      qc.invalidateQueries({ queryKey: ["adminSettings"] });
      toast.success(`Poll speed set to ${mode}`);
    } catch {
      toast.error("Couldn't update — try again");
    } finally { setSaving(""); }
  };

  const current = data?.pollMode;
  const modes = data?.modes || [];
  const est = data?.estimate;

  return (
    <div className="space-y-6">
    <div className="border border-white/10 bg-card p-5" data-testid="credits-saver">
      <div className="flex items-center gap-2 mb-1">
        <GaugeIcon size={18} weight="fill" className="text-neon" />
        <h3 className="font-heading font-bold text-sm">Credits Saver — Smart Money poll speed</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
        Controls how often we poll tracked wallets on Helius. Slower = fewer credits used. Active wallets always poll faster than dormant ones. Applies within ~1 poll cycle, no redeploy needed.
      </p>
      {isLoading ? (
        <div className="flex justify-center py-6"><SpinnerGapIcon size={22} className="animate-spin text-neon" /></div>
      ) : (
        <>
          {est && (
            <div data-testid="burn-estimate" className="flex flex-wrap items-center gap-x-6 gap-y-1 mb-4 pb-4 border-b border-white/10 font-mono text-xs">
              <span className="text-muted-foreground">Unique wallets tracked: <span className="text-white font-bold">{est.uniqueWallets}</span></span>
              <span className="text-muted-foreground">Monthly budget: <span className="text-white font-bold">{est.monthlyBudgetM}M</span></span>
              {est.modes?.[current] && (
                <span className="text-muted-foreground">
                  Est. burn (current mode): <span className={est.modes[current].fits ? "text-neon font-bold" : "text-danger font-bold"}>~{est.modes[current].typicalM}M/mo</span>
                </span>
              )}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {modes.map((m) => {
              const active = current === m.id;
              const me = est?.modes?.[m.id];
              return (
                <button
                  key={m.id}
                  data-testid={`poll-mode-${m.id}`}
                  onClick={() => pick(m.id)}
                  disabled={saving === m.id}
                  className={`text-left border p-4 transition-colors ${active ? "border-neon bg-neon/[0.06]" : "border-white/10 hover:border-white/30"}`}
                >
                  <div className="flex items-center gap-2">
                    <LightningIcon size={15} weight={active ? "fill" : "regular"} className={active ? "text-neon" : "text-muted-foreground"} />
                    <span className={`font-heading font-bold text-sm ${active ? "text-neon" : ""}`}>{m.label}</span>
                    {active && <span className="ml-auto text-[10px] font-mono px-1.5 py-0.5 bg-neon/15 text-neon border border-neon/30">ACTIVE</span>}
                  </div>
                  <div className="font-mono text-[11px] text-muted-foreground mt-2 space-y-0.5">
                    <div>Active wallets: every {m.activeSec}s</div>
                    <div>Dormant wallets: ~{m.dormantMin} min</div>
                    {me && (
                      <div className={`pt-1 ${me.fits ? "text-neon" : "text-danger"}`}>
                        ~{me.typicalM}M/mo <span className="text-muted-foreground/60">({me.lowM}–{me.highM}M)</span>
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
    <CreditMeter usage={data?.usage} />
    </div>
  );
};

function Metrics() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["adminMetrics"], queryFn: fetchAdminMetrics, refetchInterval: 60000 });
  const { data: issuesData } = useQuery({ queryKey: ["adminIssues"], queryFn: fetchAdminIssues, refetchInterval: 60000 });
  const [busy, setBusy] = useState("");

  const resolve = async (id) => {
    setBusy(id);
    try { await resolveIssue(id); qc.invalidateQueries({ queryKey: ["adminIssues"] }); qc.invalidateQueries({ queryKey: ["adminMetrics"] }); }
    catch { toast.error("Failed"); }
    setBusy("");
  };

  if (isLoading || !data) return <div className="flex justify-center py-20"><SpinnerGapIcon size={28} className="animate-spin text-neon" /></div>;

  const tw = data.thisWeek, lw = data.lastWeek;
  const pct = (v) => (v == null ? "—" : `${v}%`);
  const num = (v) => (v == null ? "—" : v.toLocaleString());

  const Row = ({ label, hint, now, prev, fmt = pct }) => {
    const delta = now != null && prev != null ? Math.round((now - prev) * 10) / 10 : null;
    const up = delta != null && delta > 0;
    const flat = delta == null || delta === 0;
    return (
      <div className="grid grid-cols-[1fr_auto_auto_auto] gap-3 items-center py-3 border-b border-white/5" data-testid={`metric-row-${label.replace(/[^a-z]/gi, "-").toLowerCase()}`}>
        <div className="min-w-0">
          <div className="text-sm font-medium">{label}</div>
          {hint && <div className="text-[10px] text-muted-foreground/70">{hint}</div>}
        </div>
        <div className="font-mono text-sm text-right w-16">{fmt(now)}</div>
        <div className="font-mono text-xs text-muted-foreground/60 text-right w-16">{fmt(prev)}</div>
        <div className={`font-mono text-xs text-right w-16 flex items-center justify-end gap-0.5 ${flat ? "text-muted-foreground/50" : up ? "text-neon" : "text-danger"}`}>
          {!flat && (up ? <ArrowUpIcon size={11} /> : <ArrowDownIcon size={11} />)}
          {delta == null ? "—" : `${Math.abs(delta)}${fmt === pct ? "pt" : ""}`}
        </div>
      </div>
    );
  };

  const src = tw.reportSources || {};
  const srcTotal = Object.values(src).reduce((a, b) => a + b, 0) || 1;
  const srcColors = { organic: "#00E599", shared: "#F5A623", direct: "#6EE7B7", referral: "#8A8F98" };
  const srcLabels = { organic: "Organic search", shared: "Shared reports", direct: "Direct", referral: "Other referral" };

  const issues = issuesData?.issues || [];
  const openIssues = issues.filter((i) => i.status === "open");

  return (
    <div className="space-y-6" data-testid="admin-metrics">
      {/* North star */}
      <div className="border border-neon/30 bg-neon/[0.05] p-6" data-testid="north-star-card">
        <div className="flex items-center gap-2 text-neon mb-1"><TargetIcon size={18} weight="fill" /><span className="text-xs font-mono uppercase tracking-wider">North-Star Metric</span></div>
        <div className="flex items-end gap-4 flex-wrap">
          <div className="font-heading font-black text-5xl leading-none">{num(data.northStar)}</div>
          <div className="text-sm text-muted-foreground pb-1">
            weekly users who did <span className="text-white">2+ research actions</span> and <span className="text-white">returned the next week</span>
            <div className="text-[11px] text-muted-foreground/70 mt-0.5">{pct(data.northStarRate)} of last week's {num(data.northStarCohort)}-user cohort returned</div>
          </div>
        </div>
      </div>

      {/* Weekly funnel table */}
      <div className="border border-white/10 bg-card p-5">
        <div className="flex items-center gap-2 mb-3"><PulseIcon size={18} className="text-neon" /><h3 className="font-heading font-bold text-lg">Weekly Scorecard</h3></div>
        <div className="grid grid-cols-[1fr_auto_auto_auto] gap-3 text-[10px] font-mono uppercase tracking-wider text-muted-foreground pb-2 border-b border-white/10">
          <div>Metric</div><div className="text-right w-16">This wk</div><div className="text-right w-16">Last wk</div><div className="text-right w-16">Δ</div>
        </div>
        <Row label="Visitor → first report" hint="visitors who opened a token report" now={tw.visitorToReportRate} prev={lw.visitorToReportRate} />
        <Row label="First → second report" hint="report viewers who read 2+ tokens" now={tw.firstToSecondReportRate} prev={lw.firstToSecondReportRate} />
        <Row label="Report share rate" hint="report viewers who shared" now={tw.shareRate} prev={lw.shareRate} />
        <Row label="Watchlist creation rate" hint="visitors who saved a coin" now={tw.watchlistRate} prev={lw.watchlistRate} />
        <Row label="Alert opt-in rate" hint="visitors who enabled alerts" now={tw.alertOptInRate} prev={lw.alertOptInRate} />
        <Row label="7-day returning users" hint="% of last week active who came back" now={data.sevenDayReturningRate} prev={null} />
        <Row label="Reports with fresh data" hint="report views served live data" now={tw.freshDataRate} prev={lw.freshDataRate} />
        <Row label="Unique visitors" hint="distinct clients" now={tw.visitors} prev={lw.visitors} fmt={num} />
        <Row label="Token report views" hint="total /coin & /token views" now={tw.reportViews} prev={lw.reportViews} fmt={num} />
      </div>

      {/* Traffic sources */}
      <div className="border border-white/10 bg-card p-5">
        <h3 className="font-heading font-bold text-lg mb-3">Report Traffic Sources (7d)</h3>
        <div className="flex h-3 w-full overflow-hidden mb-3">
          {Object.keys(srcLabels).map((k) => (
            <div key={k} style={{ width: `${(src[k] || 0) / srcTotal * 100}%`, background: srcColors[k] }} />
          ))}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Object.keys(srcLabels).map((k) => (
            <div key={k} data-testid={`src-${k}`}>
              <div className="flex items-center gap-1.5 text-xs"><span className="w-2 h-2" style={{ background: srcColors[k] }} />{srcLabels[k]}</div>
              <div className="font-mono text-sm mt-0.5">{num(src[k] || 0)} <span className="text-muted-foreground/60 text-xs">({Math.round((src[k] || 0) / srcTotal * 100)}%)</span></div>
            </div>
          ))}
        </div>
      </div>

      {/* Issues */}
      <div className="border border-white/10 bg-card p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2"><BugIcon size={18} className="text-warn" /><h3 className="font-heading font-bold text-lg">User-Reported Issues</h3></div>
          <div className="font-mono text-xs text-muted-foreground">{data.issues.open} open · {data.issues.resolved7d} resolved (7d) · {data.issues.reported7d} reported (7d)</div>
        </div>
        {openIssues.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center" data-testid="no-open-issues">No open issues 🎉</p>
        ) : (
          <div className="space-y-2">
            {openIssues.map((it) => (
              <div key={it.id} data-testid={`issue-${it.id}`} className="flex items-start justify-between gap-3 p-3 border border-white/10">
                <div className="min-w-0">
                  <p className="text-sm">{it.message}</p>
                  <p className="text-[10px] text-muted-foreground/60 font-mono mt-1">{it.path || "—"} · {it.ts ? new Date(it.ts).toLocaleString() : ""}</p>
                </div>
                <button data-testid={`resolve-issue-${it.id}`} onClick={() => resolve(it.id)} disabled={busy === it.id} className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs border border-neon/30 text-neon hover:bg-neon/10 transition-colors disabled:opacity-50">
                  <CheckCircleIcon size={14} /> Resolve
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, openAuth } = useAuth();
  const [tab, setTab] = useState("overview");
  const isAdmin = user?.role === "admin";

  const { data: stats, isLoading, isError, refetch } = useQuery({
    queryKey: ["adminStats"],
    queryFn: fetchAdminStats,
    enabled: isAdmin,
    refetchInterval: 30000,
    retry: 1,
  });

  if (!isAdmin) {
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center space-y-4">
        <ShieldCheckIcon size={48} className="text-neon mx-auto" />
        <h1 className="font-heading font-black text-2xl">Admin only</h1>
        <p className="text-muted-foreground text-sm">Log in with the admin account to view the dashboard.</p>
        <button data-testid="admin-login-btn" onClick={() => openAuth("login")} className="px-5 py-2.5 bg-neon text-black font-semibold text-sm">Log in</button>
      </div>
    );
  }

  const tabs = [
    { id: "overview", label: "Overview", icon: ChartBarIcon },
    { id: "metrics", label: "Metrics", icon: TargetIcon },
    { id: "users", label: "Registrations", icon: UsersIcon },
    { id: "helius", label: "Helius Access", icon: LightningIcon },
    { id: "removals", label: "Removals", icon: ProhibitIcon },
    { id: "graveyard", label: "Graveyard", icon: SkullIcon },
    { id: "curated", label: "Curated", icon: TrophyIcon },
    { id: "media", label: "Media", icon: ImagesIcon },
  ];

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 space-y-6 animate-fade-up">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 flex items-center justify-center bg-neon text-black"><ShieldCheckIcon size={24} weight="bold" /></div>
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">ADMIN</h1>
      </div>

      <div className="flex border border-white/10 w-fit">
        {tabs.map((t) => (
          <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)} className={`flex items-center gap-2 px-5 py-2.5 text-sm font-semibold transition-colors ${tab === t.id ? "bg-neon/10 text-neon" : "text-muted-foreground hover:text-white"}`}>
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      {isError ? (
        <div className="flex flex-col items-center justify-center py-24 gap-4 text-center" data-testid="admin-error">
          <XCircleIcon size={40} className="text-danger" />
          <p className="text-muted-foreground text-sm max-w-sm">
            Couldn't load the dashboard. Your admin session may have expired — try again, or log in with the admin account.
          </p>
          <div className="flex items-center gap-3">
            <button data-testid="admin-retry-btn" onClick={() => refetch()} className="px-5 py-2.5 border border-white/20 text-sm font-semibold hover:border-neon/50 hover:text-neon transition-colors">
              Try again
            </button>
            <button data-testid="admin-relogin-btn" onClick={() => openAuth("login")} className="px-5 py-2.5 bg-neon text-black font-semibold text-sm hover:bg-neon/90 transition-colors">
              Log in again
            </button>
          </div>
        </div>
      ) : isLoading || !stats ? (
        <div className="flex justify-center py-24"><SpinnerGapIcon size={32} className="animate-spin text-neon" /></div>
      ) : tab === "overview" ? (
        <div className="space-y-6"><Overview stats={stats} /><CreditsSaver /></div>
      ) : tab === "metrics" ? (
        <Metrics />
      ) : tab === "users" ? (
        <Users stats={stats} />
      ) : tab === "helius" ? (
        <HeliusAccess />
      ) : tab === "removals" ? (
        <Removals />
      ) : tab === "graveyard" ? (
        <Graveyard />
      ) : tab === "curated" ? (
        <Curated />
      ) : (
        <Media />
      )}
    </div>
  );
}
